import { isInvalidString, isInvalidQuantity, isInvalidCancellationReason } from "src/validation/stringValidation";
import { FormValidator } from "../formValidator";
import { ValidationError, ValidationErrors } from "src/errors";
import {
  EditClaimFormData,
  EditClaimFormDataKey,
  DAMAGE_CLAIM_TYPES,
  DISCREPANCY_CLAIM_TYPES,
  CLAIM_URGENCY_LEVELS
} from "src/types";

export class EditClaimFormValidator extends FormValidator {
  private static validateProducts(value: unknown): ValidationError<"products"> | undefined {
    if (value === undefined) {
      return undefined;
    }
    if (!Array.isArray(value)) {
      return new ValidationError("Products must be an array", "products");
    }
    // Filter out deleted products and products with zero or empty quantities - these will be automatically excluded
    const productsWithValidQuantities = value.filter((product) => {
      // Exclude deleted products
      if ((product as { deleted?: boolean })?.deleted === true) {
        return false;
      }
      const claimQty =
        typeof product?.claimQuantity === "number"
          ? product.claimQuantity
          : typeof product?.claimQuantity === "string"
            ? product.claimQuantity.trim()
            : "";
      if (!claimQty || claimQty === "0") {
        return false; // Exclude products with empty or zero quantity
      }
      const parsedQty = typeof claimQty === "string" ? parseInt(claimQty, 10) : claimQty;
      return !isNaN(parsedQty) && parsedQty > 0;
    });

    // Validate the remaining products
    for (let i = 0; i < productsWithValidQuantities.length; i++) {
      const product = productsWithValidQuantities[i] as {
        claimQuantity?: unknown;
        productName?: string;
        quantity?: number;
      };

      // Check if this product has a valid claim quantity
      const claimQty =
        typeof product?.claimQuantity === "number"
          ? product.claimQuantity.toString()
          : typeof product?.claimQuantity === "string"
            ? product.claimQuantity.trim()
            : "";

      if (isInvalidQuantity(claimQty, 1, 100000)) {
        const productName = product?.productName || `Product ${i + 1}`;
        return new ValidationError(`${productName}: Enter a valid claim quantity (1-100000)`, "products");
      }
    }

    return undefined;
  }

  private static validateIssueType(value: unknown): ValidationError<"issueType"> | undefined {
    const validIssueTypes = ["damage", "discrepancy"];
    if (typeof value !== "string" || !validIssueTypes.includes(value)) {
      return new ValidationError("Select a valid issue type", "issueType");
    }
    return undefined;
  }

  private static validateClaimDetails(
    value: unknown,
    issueType?: "damage" | "discrepancy"
  ): ValidationError<"claimDetails"> | undefined {
    if (!Array.isArray(value) || value.length === 0) {
      return new ValidationError("At least one claim detail is required", "claimDetails");
    }

    const validClaimDetails = value.filter((detail) => {
      const description = typeof detail?.detailedDescription === "string" ? detail.detailedDescription.trim() : "";
      return description.length > 0;
    });

    if (validClaimDetails.length === 0) {
      return new ValidationError("At least one claim detail with description is required", "claimDetails");
    }

    for (let i = 0; i < value.length; i++) {
      const detail = value[i] as {
        claimType?: unknown;
        urgencyLevel?: unknown;
        detailedDescription?: unknown;
        nonOrderProducts?: unknown[];
      };

      const validClaimTypes = issueType === "discrepancy" ? DISCREPANCY_CLAIM_TYPES : DAMAGE_CLAIM_TYPES;
      if (typeof detail?.claimType !== "string" || !validClaimTypes.includes(detail.claimType as any)) {
        return new ValidationError(`Claim detail ${i + 1}: Select a valid claim type`, "claimDetails");
      }

      if (typeof detail?.urgencyLevel !== "string" || !CLAIM_URGENCY_LEVELS.includes(detail.urgencyLevel as any)) {
        return new ValidationError(`Claim detail ${i + 1}: Select a valid urgency level`, "claimDetails");
      }

      const description = typeof detail?.detailedDescription === "string" ? detail.detailedDescription.trim() : "";
      if (!description || description.length === 0) {
        return new ValidationError(`Claim detail ${i + 1}: Detailed description is required`, "claimDetails");
      }
      if (isInvalidString(description, 5, 2000)) {
        return new ValidationError(
          `Claim detail ${i + 1}: Detailed description must be between 5 and 2000 characters`,
          "claimDetails"
        );
      }

      const isWrongProductClaimType = detail.claimType === "Wrong Product";

      if (isWrongProductClaimType) {
        if (
          !detail.nonOrderProducts ||
          !Array.isArray(detail.nonOrderProducts) ||
          detail.nonOrderProducts.length === 0
        ) {
          return new ValidationError(
            `Claim detail ${i + 1}: Non-order products are required for "${detail.claimType}" claim type`,
            "claimDetails"
          );
        }

        // Check if at least one non-order product has valid data
        const hasValidNonOrderProducts = detail.nonOrderProducts.some((product: any) => {
          const productId = typeof product?.productId === "string" ? product.productId.trim() : "";
          const claimQty = typeof product?.claimQuantity === "string" ? product.claimQuantity.trim() : "";
          if (!productId || !claimQty || claimQty === "0") return false;
          const parsedQty = parseInt(claimQty, 10);
          return !isNaN(parsedQty) && parsedQty > 0;
        });

        if (!hasValidNonOrderProducts) {
          return new ValidationError(
            `Claim detail ${i + 1}: At least one valid non-order product is required for "${detail.claimType}" claim type`,
            "claimDetails"
          );
        }
      }

      if (detail.nonOrderProducts && Array.isArray(detail.nonOrderProducts) && detail.nonOrderProducts.length > 0) {
        const nonOrderProducts = detail.nonOrderProducts;

        for (let j = 0; j < nonOrderProducts.length; j++) {
          const product = nonOrderProducts[j] as {
            productId?: unknown;
            claimQuantity?: unknown;
            extraInfo?: unknown;
          };

          const productId = typeof product?.productId === "string" ? product.productId.trim() : "";
          if (!productId || productId.length === 0) {
            return new ValidationError(
              `Claim detail ${i + 1}, Non-order product ${j + 1}: Product ID is required`,
              "claimDetails"
            );
          }
          if (isInvalidString(productId, 1, 100)) {
            return new ValidationError(
              `Claim detail ${i + 1}, Non-order product ${j + 1}: Product ID must be between 1 and 100 characters`,
              "claimDetails"
            );
          }

          const claimQuantity = typeof product?.claimQuantity === "string" ? product.claimQuantity.trim() : "";
          if (!claimQuantity || claimQuantity === "0") {
            return new ValidationError(
              `Claim detail ${i + 1}, Non-order product ${j + 1}: Claim quantity is required and must be greater than 0`,
              "claimDetails"
            );
          }
          if (isInvalidQuantity(claimQuantity, 1, 100000)) {
            return new ValidationError(
              `Claim detail ${i + 1}, Non-order product ${j + 1}: Enter a valid claim quantity (1-100000)`,
              "claimDetails"
            );
          }

          if (product?.extraInfo !== undefined && product.extraInfo !== null) {
            const extraInfo = typeof product.extraInfo === "string" ? product.extraInfo.trim() : "";
            if (extraInfo.length > 0 && isInvalidString(extraInfo, 0, 1000)) {
              return new ValidationError(
                `Claim detail ${i + 1}, Non-order product ${j + 1}: Extra info must be less than 1000 characters`,
                "claimDetails"
              );
            }
          }
        }
      }
    }

    return undefined;
  }

  private static validateWarehouseLocation(value: unknown): ValidationError<"warehouseLocation"> | undefined {
    if (isInvalidString(value, 2, 100)) {
      return new ValidationError("Warehouse location must be between 2 and 100 characters", "warehouseLocation");
    }
    return undefined;
  }

  private static validateRejectionReason(value: unknown): ValidationError<"rejectionReason"> | undefined {
    const stringValue = value?.toString()?.trim();
    if (!stringValue || stringValue.length === 0) {
      return undefined;
    }
    return isInvalidCancellationReason(stringValue)
      ? new ValidationError("Enter a valid rejection reason", "rejectionReason")
      : undefined;
  }

  private static readonly validationStrategies: Record<
    EditClaimFormDataKey,
    (value: unknown) => ValidationError<EditClaimFormDataKey> | undefined
  > = {
    issueType: EditClaimFormValidator.validateIssueType,
    products: EditClaimFormValidator.validateProducts,
    claimDetails: () => undefined, // Will be validated separately with issueType context
    warehouseLocation: EditClaimFormValidator.validateWarehouseLocation,
    claimImages: () => undefined, // Optional, no validation needed
    existingImageUrls: () => undefined, // Internal state, no validation needed
    deletedImageUrls: () => undefined, // Internal state, no validation needed
    rejectionReason: EditClaimFormValidator.validateRejectionReason
  };

  static validateEditClaimFormData<T extends EditClaimFormDataKey>(
    formData: Partial<Record<T, unknown>> & Partial<EditClaimFormData>
  ): ValidationErrors<T> {
    const errors = super.validateFormData(formData, EditClaimFormValidator.validationStrategies) as ValidationErrors<T>;

    const hasProductsField = "products" in formData;
    const hasClaimDetailsField = "claimDetails" in formData;

    if (hasProductsField || hasClaimDetailsField) {
      const hasNonOrderProducts = formData.claimDetails?.some((detail: any) => {
        if (
          !detail?.nonOrderProducts ||
          !Array.isArray(detail.nonOrderProducts) ||
          detail.nonOrderProducts.length === 0
        ) {
          return false;
        }
        return detail.nonOrderProducts.some((product: any) => {
          const productId = typeof product?.productId === "string" ? product.productId.trim() : "";
          const claimQty = typeof product?.claimQuantity === "string" ? product.claimQuantity.trim() : "";
          if (!productId || !claimQty || claimQty === "0") return false;
          const parsedQty = parseInt(claimQty, 10);
          return !isNaN(parsedQty) && parsedQty > 0;
        });
      });

      const hasRegularProducts = Array.isArray(formData.products) && formData.products.length > 0;
      const productsWithValidQuantities = hasRegularProducts
        ? (formData.products as any[]).filter((product) => {
            if (product?.deleted === true) return false;
            const claimQty =
              typeof product?.claimQuantity === "number"
                ? product.claimQuantity
                : typeof product?.claimQuantity === "string"
                  ? product.claimQuantity.trim()
                  : "";
            if (!claimQty || claimQty === "0") return false;
            const parsedQty = typeof claimQty === "string" ? parseInt(claimQty, 10) : claimQty;
            return !isNaN(parsedQty) && parsedQty > 0;
          })
        : [];

      if (!hasNonOrderProducts && productsWithValidQuantities.length === 0) {
        const productsErrorIndex = errors.findIndex((error) => error.formDataKey === "products");
        if (productsErrorIndex === -1) {
          errors.push(
            new ValidationError(
              "At least one product with claim quantity is required, or add non-order products",
              "products"
            ) as ValidationError<T>
          );
        }
      }
    }

    if (formData.claimDetails !== undefined && formData.issueType !== undefined && formData.claimDetails.length > 0) {
      const claimDetailsError = EditClaimFormValidator.validateClaimDetails(formData.claimDetails, formData.issueType);
      if (claimDetailsError) {
        errors.push(claimDetailsError as ValidationError<T>);
      }
    }
    return errors;
  }
}
